import { getStatusMetadata } from '../vacations.definitions';
import type { VacationDpRow } from '../models/vacationDpBoard.types';
import { daysUntilDeadline, isExpired } from './vacationDpStatus';

/**
 * Faixas da ordenação padrão do painel, da mais urgente para a menos.
 *
 * `Expired` primeiro porque prazo vencido é exposição a pagamento em dobro (art. 137) — o único
 * item da tela que já custa dinheiro. `NoDeadline` fecha a lista: quem ainda está no primeiro
 * período aquisitivo não tem nada a cobrar.
 */
export const VACATION_URGENCY_RANK = {
    Expired: 0,
    Upcoming: 1,
    NoDeadline: 2,
} as const;

export type VacationUrgencyRank =
    (typeof VACATION_URGENCY_RANK)[keyof typeof VACATION_URGENCY_RANK];

export function getVacationUrgencyRank(
    row: VacationDpRow,
    today: Date = new Date()
): VacationUrgencyRank {
    if (isExpired(row, today)) return VACATION_URGENCY_RANK.Expired;
    if (daysUntilDeadline(row, today) !== null) return VACATION_URGENCY_RANK.Upcoming;
    return VACATION_URGENCY_RANK.NoDeadline;
}

/**
 * Desempate por etapa da solicitação.
 *
 * Sem solicitação vem antes de tudo (`0`): com o mesmo prazo, quem nem pediu é o caso que exige
 * ação do DP hoje — os demais já estão andando em alguma mesa. Daí para frente vale o `rank` de
 * `STATUS_METADATA`, que já ordena o ciclo do pedido parado no gestor até o cancelado.
 */
export function getStatusRank(row: VacationDpRow): number {
    if (!row.request) return 0;
    return getStatusMetadata(row.request.status).rank;
}

/**
 * Ordem padrão da tabela: vencidos primeiro (do mais antigo), depois por proximidade do vencimento.
 *
 * Dentro da mesma faixa a comparação é pelo prazo em ordem crescente — o que torna "mais antigo
 * primeiro" e "mais próximo primeiro" a mesma regra, porque um prazo vencido há muito tempo é a
 * menor data da lista. Empates caem na etapa e, por fim, no nome, para a ordem não oscilar entre
 * renderizações.
 */
export function compareVacationUrgency(
    a: VacationDpRow,
    b: VacationDpRow,
    today: Date = new Date()
): number {
    const rankA = getVacationUrgencyRank(a, today);
    const rankB = getVacationUrgencyRank(b, today);
    if (rankA !== rankB) return rankA - rankB;

    const daysA = daysUntilDeadline(a, today);
    const daysB = daysUntilDeadline(b, today);
    // Vencido sem prazo registrado (só `expiredDays`) vai para o fim da própria faixa: não há data
    // para comparar, e chutar uma inventaria uma ordem que o dado não sustenta.
    if (daysA !== daysB) {
        if (daysA === null) return 1;
        if (daysB === null) return -1;
        return daysA - daysB;
    }

    const statusDiff = getStatusRank(a) - getStatusRank(b);
    if (statusDiff !== 0) return statusDiff;

    return a.collaboratorName.localeCompare(b.collaboratorName, 'pt-BR');
}

export function sortByVacationUrgency(
    rows: VacationDpRow[],
    today: Date = new Date()
): VacationDpRow[] {
    return [...rows].sort((a, b) => compareVacationUrgency(a, b, today));
}
